import { createContext, useContext, useLayoutEffect, useState } from 'react'
export interface TabScope {
  id: string
  /** Mutable ownership is read by event/async callbacks, including during a selection commit. */
  ownsInteraction(): boolean
  title(token: symbol, title: string | null | undefined): void
  /** `page` owns the edit (pageOf): only leaving that page discards it. */
  dirty(token: symbol, dirty: boolean, page?: string): void
  /** Any unsaved edit, or only those owned by `pages`. */
  hasUnsaved(pages?: readonly string[]): boolean
  surface(): HTMLElement | null
  path(): string
  /** Full address kept under Settings, including while the frame is remounted or suspended. */
  background(): string
  navigate(path: string, replace?: boolean): void
  readDraft(path: string, key: string): string | undefined
  writeDraft(path: string, key: string, value?: string, dirty?: boolean): void
  checkpoint(): Promise<boolean>
  reload(): void
}
export const TabContext = createContext<TabScope | null>(null)
/** The pathname of the page a component is drawn on: the page under Settings, or the sheet's (App.tsx). */
export const SurfaceContext = createContext<string | null>(null)
export const useSurface = () => useContext(SurfaceContext)
export const TabOwnerContext = createContext(true)
export const useTabOwner = () => useContext(TabOwnerContext)
export const useTabScope = () => useContext(TabContext)
/** A callback can neither focus nor handle a global shortcut after its tab loses ownership. */
export function canInteract(scope: TabScope | null) { return scope === null || scope.ownsInteraction() }
export function usePortalTarget() {
  const scope = useTabScope()
  const [target, setTarget] = useState<HTMLElement | null>(scope ? null : document.body)
  useLayoutEffect(() => { setTarget(scope?.surface() ?? document.body) }, [scope])
  return target
}
